import {
  Chip,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  formatSkillDate,
  getSkillAuthorName,
  skillVisibilityChipColorByVisibility,
  skillVisibilityIconByVisibility,
  skillVisibilityLabel,
} from "../skill-utils";
import type { SkillListItemProps } from "./types";

const skillsListChipSx = {
  flexShrink: 0,
  justifyContent: "flex-start",
  "& .MuiChip-label": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

export const SkillsListItem = ({
  skill,
  selected,
  onSelect,
}: SkillListItemProps) => {
  const authorName = getSkillAuthorName(skill);
  const updatedDate = formatSkillDate(skill.updatedAt);
  const VisibilityIcon = skillVisibilityIconByVisibility[skill.visibility];

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(skill.id)}
      sx={{
        alignItems: "flex-start",
        borderLeft: "3px solid",
        borderLeftColor: selected ? "rgba(0, 0, 0, 0.12)" : "transparent",
        borderRadius: 0,
        cursor: "pointer",
        gap: 1,
        m: 0,
        py: 1.25,
        "&.Mui-selected": {
          backgroundColor: "#f5f5f5",
        },
        "&.Mui-selected:hover": {
          backgroundColor: "#f5f5f5",
        },
      }}
      data-testid="skills-list-item"
    >
      <ListItemText
        sx={{ m: 0 }}
        data-testid="skills-list-item-text"
        primaryTypographyProps={{ component: "div" }}
        secondaryTypographyProps={{ component: "div" }}
        primary={
          <Typography
            variant="body1"
            fontWeight={600}
            noWrap
            sx={{ minWidth: 0 }}
          >
            {skill.name}
          </Typography>
        }
        secondary={
          <Stack gap={0.5} sx={{ mt: 0.5 }}>
            <Stack
              direction="row"
              gap={0.75}
              alignItems="center"
              flexWrap="wrap"
              data-testid="skills-list-item-chips"
            >
              <Chip
                icon={<VisibilityIcon fontSize="small" />}
                label={skillVisibilityLabel[skill.visibility]}
                color={skillVisibilityChipColorByVisibility[skill.visibility]}
                variant="outlined"
                size="xxs"
                sx={skillsListChipSx}
              />
              {!skill.enabled ? (
                <Chip label="Disabled" size="xxs" sx={skillsListChipSx} />
              ) : null}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {skill.descriptionMd}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {authorName ? `${authorName} - ${updatedDate}` : updatedDate}
            </Typography>
          </Stack>
        }
      />
    </ListItemButton>
  );
};
